/**
 * Widget-processor factory.
 *
 * Produces the `(set) => (messageId, widget) => void` shape consumed by every widget
 * processor in `features/widgets/agents/<W>/processors/`. The factory takes
 * an explicit `MakeCreateWidgetActionDeps` argument so the entity remains
 * dependency-free of `@/features/`, `@/app/`, and `@/lib/`.
 *
 * **Dep-inversion rationale**: the previous implementation
 * (`features/widgets/processors/widget-processor.utils.ts`) value-imported
 * `useChatStore` (chats slice), `useUIStore` (app/store), and
 * `getAgentTitleSync` (lib/config). All three violated FSD layer order. Those
 * concerns are now passed in as callbacks at every call site.
 *
 * **Behavior preserved**: the factory output is functionally identical to the
 * previous direct utility — same setTimeout(200) side-panel-open delay, same
 * `_pendingWidgets` staging payload shape (`{ agentName, widget }`).
 *
 * Call sites build the dep object via the shared
 * `widgetProcessorDeps` helper at
 * `features/widgets/processors/widget-processor.deps.ts` (features-layer
 * file — allowed to value-import the cross-layer concerns), then call
 * `makeCreateWidgetAction(widgetProcessorDeps)` to produce the processor.
 */
import { WidgetStateSetter, WidgetWithMeta } from './widget.types';

export type WidgetProcessorFactory<T> = (
  set: WidgetStateSetter,
) => (messageId: string, widget: WidgetWithMeta<T>) => void;

/**
 * Payload staged on the chat store under `_pendingWidgets[messageId]` so the
 * chat response handler can later associate the rendered widget with the
 * incoming message exchange.
 */
export interface PendingWidgetPayload {
  agentName: string;
  widget: WidgetWithMeta;
}

/**
 * Dependency surface for `makeCreateWidgetAction`. Each callback abstracts
 * exactly one cross-layer concern that previously violated FSD layer order
 * when value-imported directly inside this entity slice.
 */
export interface MakeCreateWidgetActionDeps {
  /** Returns whether the chat is currently connected (typically `useChatStore.getState().isConnected`). */
  getIsChatConnected: () => boolean;
  /** Opens the widget side panel (typically `() => useUIStore.getState().openSidePanel('widget')`). */
  openWidgetSidePanel: () => void;
  /** Stages a pending widget on the chat store under the given message id. */
  stagePendingWidget: (messageId: string, payload: PendingWidgetPayload) => void;
}

/**
 * Build a widget-processor factory bound to the supplied dependency callbacks.
 *
 * The result is a `WidgetProcessorFactory<unknown>` — a function `(set) =>
 * (messageId, widget) => void` — directly assignable to the `processor` field
 * of a `WidgetActionProcessor<...>`. Behavior matches the previous
 * non-inverted `makeCreateWidgetAction` exactly.
 */
export const makeCreateWidgetAction = (
  deps: MakeCreateWidgetActionDeps,
): WidgetProcessorFactory<unknown> => {
  return <T>(set: WidgetStateSetter) => {
    return (messageId: string, widget: WidgetWithMeta<T>) => {
      set({
        widget: widget as WidgetWithMeta,
        loading: false,
        error: null,
      });

      setTimeout(() => {
        // Open side panel if connected (remove waiting check since it creates timing issues)
        if (deps.getIsChatConnected()) {
          deps.openWidgetSidePanel();
        }
      }, 200); // Delay to ensure message rendering is complete

      // Store widget info temporarily for later processing in response handler
      if (messageId) {
        deps.stagePendingWidget(messageId, {
          agentName: widget.agentName,
          widget: widget as WidgetWithMeta,
        });
      }
    };
  };
};
